import { Link } from 'react-router-dom'
import { ArrowRightLeft, Server, FileCheck2, KeyRound } from 'lucide-react'
import SeoHead, { ORGANIZATION_LD } from '../components/SeoHead'
import { SITE, mailto } from '../lib/site'

// Herkese açık entegrasyon dokümanı. Bilinçli olarak auth arkasında DEĞİL:
// entegratör satışının önkoşulu ve rakiplerin çoğunun sahip olmadığı yüzey.
//
// Kapsam kuralı (2026-06-27 sadeleştirme kararı): burada kütüphane/paket/dağıtım
// anlatılmaz, fiyat yazılmaz. Yalnızca HTTP entegrasyon yüzeyi — uçlar gerçek
// controller'lardan alınmıştır, uydurulmuş alan yoktur.

const API = 'https://api.sign.wsoft.tr'

const JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'W.Sign geliştirici dokümantasyonu',
    description:
      'W.Sign e-imza entegrasyonu: yönlendirmeli imza oturumu, özet üzerinden imzalama API’si ve imza doğrulama ucu.',
    inLanguage: 'tr-TR',
    url: 'https://sign.wsoft.tr/docs',
    publisher: ORGANIZATION_LD,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'E-imzayı kendi uygulamama nasıl eklerim?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'İki yol var. Kullanıcıyı imza için W.Sign’a yönlendirip sonucu geri almak isterseniz yönlendirmeli imza oturumunu kullanırsınız — 3D Secure akışına benzer, kullanıcı tarafında kod yazmanız gerekmez. Kendi arayüzünüzü koruyup imzayı sunucudan yönetmek isterseniz özet üzerinden imzalama API’sini kullanırsınız.',
        },
      },
      {
        '@type': 'Question',
        name: 'Belgeler imzalanırken kullanıcının bilgisayarına iniyor mu?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Hayır. İmza iki aşamalıdır: sunucu imzalanacak veriyi hazırlar, kullanıcının kartı yalnızca birkaç yüz baytlık bu veriyi imzalar, imzalı yapı yine sunucuda kurulur. Belge istemciye hiç inmez.',
        },
      },
      {
        '@type': 'Question',
        name: 'Zaman damgası için kendi hesabımı kullanabilir miyim?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Evet. Her entegratör kendi RFC 3161 zaman damgası sunucusu bilgisini geliştirici konsolundan tanımlar; damga sizin kontörünüzden düşer.',
        },
      },
      {
        '@type': 'Question',
        name: 'İmza sonucunu kapalı ağdaki sunucuma nasıl alırım?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Webhook zorunlu değildir. Sonucu tarayıcı üzerinden POST ile alabilir (returnMode=post) ya da oturum kimliğiyle sonuç ucundan çekebilirsiniz. Böylece dışarıdan erişilemeyen sunucular da entegre olabilir.',
        },
      },
    ],
  },
]

export default function DocsPage() {
  return (
    <>
      <SeoHead
        title="Geliştirici dokümantasyonu — e-imzayı kendi uygulamanıza ekleyin"
        description="W.Sign entegrasyon dokümanı: yönlendirmeli imza oturumu (3D Secure benzeri), özet üzerinden imzalama API’si ve imza doğrulama ucu. CAdES, XAdES, PAdES; RFC 3161 zaman damgası; kendi zaman damgası hesabınızla."
        path="/docs"
        jsonLd={JSON_LD}
      />

      <section className="container-page pt-16 pb-8 md:pt-20">
        <div className="max-w-2xl">
          <span className="badge">Geliştirici</span>
          <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
            E-imzayı kendi uygulamanıza ekleyin
          </h1>
          <p className="mt-4 text-lg text-[var(--color-fg-muted)]">
            W.Sign, imzanın zor kısmını üstlenir: imzalı özniteliklerin kurulması, zaman damgası,
            sertifika zinciri ve iptal kontrolü, uzun dönemli imza yapıları. Sizin tarafınızda
            kalan iş bir HTTP çağrısı.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/login" className="btn-primary">Geliştirici konsoluna git</Link>
            <a href={mailto(SITE.email.sales, 'W.Sign entegrasyon')} className="btn-ghost">
              Entegrasyon için görüşelim
            </a>
          </div>
        </div>
      </section>

      {/* Üç yol */}
      <section className="container-page py-8">
        <h2 className="text-2xl font-semibold">Üç entegrasyon yolu</h2>
        <p className="mt-2 max-w-2xl text-[var(--color-fg-muted)]">
          İhtiyacınıza en yakın olanı seçin; üçü de aynı imza altyapısını kullanır.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <PathCard
            icon={ArrowRightLeft}
            title="Yönlendirmeli imza"
            sub="Kullanıcı imzalayacaksa"
            body="Kullanıcıyı imza sayfasına yönlendirirsiniz, imza bitince uygulamanıza döner. 3D Secure akışına benzer; imza arayüzünü siz yazmazsınız."
            anchor="#yonlendirmeli"
          />
          <PathCard
            icon={Server}
            title="Özet üzerinden imzalama"
            sub="Kendi arayüzünüzü koruyacaksanız"
            body="Sunucu imzalanacak veriyi hazırlar, imza kullanıcının kartında üretilir, imzalı yapıyı sunucu tamamlar. Belge istemciye inmez."
            anchor="#ozet-imza"
          />
          <PathCard
            icon={FileCheck2}
            title="Doğrulama"
            sub="Gelen imzayı kontrol edecekseniz"
            body="İmzalı PDF, CAdES veya XAdES dosyasını gönderin; imza geçerliliği, imzalayan sertifika ve zaman damgası bilgisi dönsün."
            anchor="#dogrulama"
          />
        </div>
      </section>

      {/* Başlarken */}
      <section className="container-page py-12 border-t border-[var(--color-border)]">
        <h2 id="baslarken" className="text-2xl font-semibold">Başlarken</h2>
        <ol className="mt-6 space-y-4">
          <Step n={1} title="Geliştirici konsolunda entegratör kaydı açın">
            Portala giriş yapıp <strong>Geliştirici</strong> bölümünden kendi entegratör kaydınızı
            oluşturursunuz. Kayıt açıldığında bir API anahtarı üretilir; anahtar yalnızca bir kez
            gösterilir, kaybederseniz konsoldan yeniden üretebilirsiniz.
          </Step>
          <Step n={2} title="İzin listelerinizi tanımlayın">
            Kullanıcının imza sonrası döneceği adresler ve — kullanacaksanız — webhook adresiniz
            alan adı bazında izin listesine girer. Listede olmayan bir adrese yönlendirme yapılmaz;
            bu, oturumunuzun başka bir siteye taşınmasını engeller.
          </Step>
          <Step n={3} title="Zaman damgası hesabınızı girin">
            Zaman damgalı ve uzun dönemli profiller için RFC 3161 sunucu adresiniz ve kimlik
            bilgileriniz konsoldan tanımlanır. Damga <strong>sizin kontörünüzden</strong> düşer;
            parola şifreli saklanır ve bir daha okunamaz.
          </Step>
          <Step n={4} title="İsteklerinizi API anahtarıyla imzalayın">
            Sunucudan sunucuya yapılan tüm çağrılarda anahtarı başlıkta gönderirsiniz:
            <Code className="mt-2 block">X-WSign-Api-Key: &lt;api-anahtarınız&gt;</Code>
            Anahtar yalnızca sunucu tarafında durmalıdır — tarayıcıya koymayın.
          </Step>
        </ol>
      </section>

      {/* Yönlendirmeli imza */}
      <section className="container-page py-12 border-t border-[var(--color-border)]">
        <div className="flex items-center gap-2">
          <ArrowRightLeft size={20} className="text-[var(--color-accent)]" />
          <h2 id="yonlendirmeli" className="text-2xl font-semibold">Yönlendirmeli imza oturumu</h2>
        </div>
        <p className="mt-3 max-w-3xl text-[var(--color-fg-muted)]">
          Kullanıcıya imza attırmanın en kısa yolu. Belgeyi ve dönüş adreslerinizi gönderir,
          karşılığında bir imza bağlantısı alırsınız; kullanıcıyı oraya yönlendirirsiniz. İmza
          tamamlandığında kullanıcı sizin adresinize döner ve sonucu üç yoldan biriyle alırsınız.
        </p>

        <h3 className="mt-8 font-semibold">1. Oturumu açın</h3>
        <Code block>{`POST ${API}/v1/redirect-sign/sessions
X-WSign-Api-Key: <api-anahtarınız>
Content-Type: application/json

{
  "documentBase64":     "<belge içeriği, base64>",
  "documentName":       "Sozlesme.pdf",
  "successRedirectUrl": "https://uygulamaniz.com/imza/tamam",
  "cancelRedirectUrl":  "https://uygulamaniz.com/imza/iptal",
  "nonce":              "<base64, en az 16 bayt rastgele>",
  "signatureProfile":   "CAdES-T",
  "ttlMinutes":         15
}`}</Code>
        <p className="mt-3 text-sm text-[var(--color-fg-muted)]">
          <strong>201 Created</strong> yanıtında <Code>sessionId</Code>, kullanıcıyı göndereceğiniz{' '}
          <Code>redirectUrl</Code>, <Code>expiresAt</Code> ve zaman damgası uygulanıp
          uygulanmayacağını belirten <Code>timestamped</Code> döner.
        </p>

        <h3 className="mt-8 font-semibold">2. Kullanıcıyı yönlendirin</h3>
        <p className="mt-2 max-w-3xl text-[var(--color-fg-muted)]">
          Kullanıcı imza sayfasında belgeyi görür, kartını takar ve PIN'ini girer. İmza kartın
          üzerinde üretilir; özel anahtar hiçbir aşamada cihazdan çıkmaz. Ardından belirttiğiniz
          adrese döner.
        </p>

        <h3 className="mt-8 font-semibold">3. Sonucu alın — üç yol</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <MiniCard title="Webhook">
            Sonuç sunucunuza POST edilir. Gövde{' '}
            <Code>X-WSign-Signature: sha256=&lt;hex&gt;</Code> başlığıyla HMAC-SHA256 imzalanır;
            doğrulamadan işlemeyin.
          </MiniCard>
          <MiniCard title="Tarayıcı POST'u">
            Oturumu <Code>returnMode=post</Code> ile açarsanız sonuç kullanıcının tarayıcısı
            üzerinden adresinize POST edilir. Dışarıdan erişilemeyen sunucular için.
          </MiniCard>
          <MiniCard title="Sonucu çekme">
            Oturum kimliğiyle sonuç ucundan çekersiniz. Webhook tanımlamak zorunda değilsiniz.
          </MiniCard>
        </div>
        <p className="mt-4 max-w-3xl text-sm text-[var(--color-fg-muted)]">
          Üç yolun da taşıdığı yük aynıdır ve aynı HMAC ile imzalanır — birinden diğerine geçmek
          için doğrulama kodunuzu değiştirmeniz gerekmez. Gönderdiğiniz <Code>nonce</Code> yükte
          geri döner; oturumu kendi kaydınızla eşleştirmek için kullanın.
        </p>
      </section>

      {/* Özet üzerinden imzalama */}
      <section className="container-page py-12 border-t border-[var(--color-border)]">
        <div className="flex items-center gap-2">
          <Server size={20} className="text-[var(--color-accent)]" />
          <h2 id="ozet-imza" className="text-2xl font-semibold">Özet üzerinden imzalama</h2>
        </div>
        <p className="mt-3 max-w-3xl text-[var(--color-fg-muted)]">
          Kendi arayüzünüzü koruyarak imzalamak istediğinizde. İki adımlıdır: sunucu imzalanacak
          veriyi hazırlar, kartın ürettiği ham imzayı geri gönderirsiniz, imzalı yapıyı sunucu
          tamamlar. <strong>Belge kullanıcının bilgisayarına inmez</strong>, imza mantığı sizde
          değil bizde kalır.
        </p>

        <h3 className="mt-8 font-semibold">Adım 1 — hazırla</h3>
        <Code block>{`POST ${API}/v1/sign/hash/prepare
X-WSign-Api-Key: <api-anahtarınız>
Content-Type: application/json

{
  "algorithm":       "CAdES-T",
  "content":         "<belge içeriği, base64>",
  "certificateId":   "<imzalayanın sertifikası>",
  "digestAlgorithm": "SHA-256"
}`}</Code>
        <p className="mt-3 text-sm text-[var(--color-fg-muted)]">
          Yanıt: <Code>sessionId</Code>, karta imzalatacağınız <Code>dataToSign</Code> (base64) ve
          oturumun geçerlilik süresi.
        </p>

        <h3 className="mt-8 font-semibold">Adım 2 — tamamla</h3>
        <p className="mt-2 max-w-3xl text-[var(--color-fg-muted)]">
          Kartın ürettiği ham imzayı <Code>sessionId</Code> ile birlikte{' '}
          <Code>/v1/sign/hash/complete</Code> ucuna gönderirsiniz. Yanıtta imzalı yapı ve imzalayan
          sertifika döner; zaman damgası, zincir ve iptal verisi seçtiğiniz profile göre içine
          yerleştirilmiş olur.
        </p>

        <h3 className="mt-8 font-semibold">Profiller</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left">
                <th className="py-3 pr-4 font-medium text-[var(--color-fg-muted)]">Profil</th>
                <th className="py-3 px-4 font-medium text-[var(--color-fg-muted)]">Ne verir</th>
                <th className="py-3 px-4 font-medium text-[var(--color-fg-muted)]">Gereksinim</th>
              </tr>
            </thead>
            <tbody>
              <ProfileRow p="CAdES-BES" what="Temel imza." need="—" />
              <ProfileRow p="CAdES-T" what="Zaman damgalı imza; imzanın ne zaman atıldığı kanıtlanır." need="Zaman damgası hesabı" />
              <ProfileRow
                p="CAdES-ESXLong"
                what="Uzun dönemli imza; sertifika ve iptal bilgisi imzanın içine gömülür, sertifikanın süresi dolduktan sonra da doğrulanır."
                need="Zaman damgası hesabı"
              />
              <ProfileRow p="XAdES-B-B" what="XML belgeler için zarflanmış imza." need="Belge iyi biçimli XML olmalı" />
              <ProfileRow p="XAdES-ESXLong" what="XML için uzun dönemli imza." need="Zaman damgası hesabı + XML" />
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-[var(--color-fg-muted)]">
          PDF yüklediğinizde portal akışı imzayı PDF'in içine gömer (PAdES) ve uzun dönemli
          seviyede üretir; imza, standart PDF görüntüleyicilerin imza panelinde görünür.
        </p>
      </section>

      {/* Doğrulama */}
      <section className="container-page py-12 border-t border-[var(--color-border)]">
        <div className="flex items-center gap-2">
          <FileCheck2 size={20} className="text-[var(--color-accent)]" />
          <h2 id="dogrulama" className="text-2xl font-semibold">Doğrulama</h2>
        </div>
        <p className="mt-3 max-w-3xl text-[var(--color-fg-muted)]">
          Doğrulama ucu <strong>API anahtarı istemez</strong>. İmzalı PDF, CAdES (<Code>.p7s</Code>,{' '}
          <Code>.cms</Code>, <Code>.p7m</Code>) veya XAdES (<Code>.xml</Code>) dosyasını gönderirsiniz;
          imzanın geçerliliği, imzalayan sertifika, zaman damgası ve uzun dönem doğrulama bilgileri
          döner. Belgede birden fazla imza varsa hepsi listelenir.
        </p>
        <Code block className="mt-4">{`POST ${API}/v1/verify/upload
Content-Type: multipart/form-data

file=@sozlesme.pdf
# ayrık imzada orijinal dosyayı da ekleyin:
# detached=@sozlesme.pdf.p7s`}</Code>
        <p className="mt-4 text-sm text-[var(--color-fg-muted)]">
          Aynı işi tarayıcıdan denemek için{' '}
          <Link to="/verify" className="text-[var(--color-accent)] hover:underline">
            imza doğrulayıcıyı
          </Link>{' '}
          kullanabilirsiniz — kurulum ve hesap gerekmez.
        </p>
      </section>

      {/* Limitler + hatalar */}
      <section className="container-page py-12 border-t border-[var(--color-border)]">
        <h2 id="limitler" className="text-2xl font-semibold">Limitler ve hata kodları</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="font-semibold">Limitler</h3>
            <ul className="mt-3 space-y-2 text-sm text-[var(--color-fg-muted)]">
              <li>• Belge boyutu: <strong>50 MB</strong> (API), doğrulamada da aynı tavan.</li>
              <li>• Oturum ömrü: varsayılan <strong>15 dakika</strong>, açarken değiştirilebilir.</li>
              <li>• Aylık oturum kotası entegratör başına tanımlıdır; konsoldan görürsünüz.</li>
              <li>• İstek hızı sınırlıdır; sınıra takılırsanız <Code>429</Code> ve{' '}
                <Code>Retry-After</Code> alırsınız.</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold">Sık karşılaşılan hatalar</h3>
            <ul className="mt-3 space-y-2 text-sm text-[var(--color-fg-muted)]">
              <li>• <Code>TSA_NOT_CONFIGURED</Code> — damgalı profil seçtiniz ama zaman damgası
                hesabınız tanımlı değil.</li>
              <li>• <Code>CALLBACK_NOT_CONFIGURED</Code> — webhook adresi gönderdiniz ama izin
                listenizde webhook alan adı yok.</li>
              <li>• <Code>INVALID_XML_CONTENT</Code> — XAdES seçtiniz ama belge geçerli XML değil.</li>
              <li>• <Code>INVALID_NONCE</Code> — nonce base64 olmalı ve en az 16 bayta çözülmeli.</li>
              <li>• <Code>QUOTA_EXCEEDED</Code> — aylık oturum kotanız dolmuş.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-page py-12 pb-20">
        <div className="card bg-[var(--color-accent-wash)] border-[var(--color-accent)]/20 flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <KeyRound size={18} className="text-[var(--color-accent)]" />
              <h2 className="text-xl font-semibold">Denemeye hazır mısınız?</h2>
            </div>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
              Geliştirici konsolundan kendi entegratör kaydınızı açın; kurumsal kurulum ve kapalı
              ağ senaryoları için bize yazın.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link to="/login" className="btn-primary">Konsola git</Link>
            <Link to="/destek" className="btn-ghost">İletişime geç</Link>
          </div>
        </div>
      </section>
    </>
  )
}

function PathCard({ icon: Icon, title, sub, body, anchor }: {
  icon: React.ComponentType<{ size?: number; className?: string }>
  title: string; sub: string; body: string; anchor: string
}) {
  return (
    <a href={anchor} className="card transition-colors hover:border-[var(--color-accent)]/40">
      <Icon size={22} className="text-[var(--color-accent)]" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="text-xs text-[var(--color-fg-dim)]">{sub}</p>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{body}</p>
    </a>
  )
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--color-accent-wash)] text-sm font-semibold text-[var(--color-accent)]">
        {n}
      </span>
      <div className="min-w-0">
        <h3 className="font-semibold">{title}</h3>
        <div className="mt-1 text-sm text-[var(--color-fg-muted)]">{children}</div>
      </div>
    </li>
  )
}

function MiniCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h4 className="font-semibold text-sm">{title}</h4>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{children}</p>
    </div>
  )
}

function ProfileRow({ p, what, need }: { p: string; what: string; need: string }) {
  return (
    <tr className="border-b border-[var(--color-border)]">
      <td className="py-3 pr-4 font-mono text-xs font-medium whitespace-nowrap">{p}</td>
      <td className="py-3 px-4 text-[var(--color-fg-muted)]">{what}</td>
      <td className="py-3 px-4 text-[var(--color-fg-muted)] whitespace-nowrap">{need}</td>
    </tr>
  )
}

function Code({ children, block, className = '' }: {
  children: React.ReactNode; block?: boolean; className?: string
}) {
  if (block) {
    return (
      <pre className={`mt-4 overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-4 text-xs leading-relaxed ${className}`}>
        <code className="font-mono">{children}</code>
      </pre>
    )
  }
  return (
    <code className={`rounded bg-[var(--color-bg-soft)] px-1.5 py-0.5 font-mono text-[0.85em] ${className}`}>
      {children}
    </code>
  )
}
